import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { ContentAssetCreateInput } from "@colab/shared";

// The full create payload minus the file-related fields the upload step
// derives (`file_url`). The caller passes the rest of the metadata + the
// File itself; the hook synthesizes `file_url: content/<storageKey>` after
// the direct PUT succeeds.
//
// Phase 7 sub-bucket 13 adds `onProgress` for the AddAssetMenu progress
// bar. Optional; callers that don't care can omit it and pay zero
// additional cost — the XHR PUT runs whether the callback is wired up or
// not.
export type UploadContentAssetInput = Omit<ContentAssetCreateInput, "file_url"> & {
  file: File;
  onProgress?: (pct: number) => void;
};

type UploadInitResponse = {
  storageKey: string;
  token: string;
};

const CONTENT_BUCKET = "content";

// Direct PUT to Supabase Storage via the one-time signed upload URL. We
// construct the URL ourselves (matching the format supabase-js builds
// internally for `uploadToSignedUrl`) so we can use XMLHttpRequest's
// upload.progress event. The fetch API + supabase-js client both surface
// only the response, not the request stream's progress.
//
// Bypassing the supabase-js wrapper is acceptable here because:
//   1. The endpoint shape is documented + stable (storage v1 routes).
//   2. The token is single-use and minted by the worker; no auth header.
//   3. Any failure surfaces as a non-2xx XHR completion → ApiClientError,
//      which matches the legacy upload path's error shape.
function putWithProgress(
  signedUrl: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", signedUrl);
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    if (onProgress) {
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
    }
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Final tick to make sure observers see 100% even when the network
        // collapses the last progress event into the load event.
        if (onProgress) onProgress(100);
        resolve();
      } else {
        reject(new ApiClientError(xhr.status, "upload_failed", xhr.responseText || "Upload failed"));
      }
    });
    xhr.addEventListener("error", () => {
      reject(new ApiClientError(0, "upload_failed", "Network error during upload"));
    });
    xhr.addEventListener("abort", () => {
      reject(new ApiClientError(0, "upload_aborted", "Upload aborted"));
    });
    xhr.send(file);
  });
}

// Three-step upload dance (bucket 4):
//
//   1. POST /api/admin/content/upload-init  →  worker mints a one-time
//      signed upload URL via Supabase Storage; returns { storageKey, token }.
//   2. PUT {SUPABASE_URL}/storage/v1/object/upload/sign/{bucket}/{key}?token=<t>
//      — direct PUT from the SPA via XMLHttpRequest so we can plumb
//      `upload.progress` to the optional onProgress callback. Failure
//      surfaces to the user; no row is created → no orphan in content_assets.
//   3. POST /api/admin/content/assets  →  insert the row with `file_url`
//      set to `content/<storageKey>`. Standard insert path (same as folder /
//      external / embed creates use via useCreateAsset).
//
// If step 3 fails after step 2 succeeds, the Storage object is orphaned (no
// row references it). Legacy did a best-effort cleanup; the new path defers
// to a future periodic sweep since the cleanup would need a separate
// authenticated round-trip from the SPA.
export function useUploadContentAsset() {
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, ApiClientError, UploadContentAssetInput>({
    mutationFn: async ({ file, onProgress, ...metadata }) => {
      // Step 1 — init.
      const init = await apiFetch<UploadInitResponse>(
        "/admin/content/upload-init",
        {
          method: "POST",
          body: JSON.stringify({
            filename: file.name,
            content_type: file.type || undefined,
            size: file.size,
          }),
        },
      );

      // Step 2 — direct PUT to Supabase Storage via the one-time token.
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new ApiClientError(0, "config_missing", "VITE_SUPABASE_URL not set");
      }
      const signedUrl = `${supabaseUrl}/storage/v1/object/upload/sign/${CONTENT_BUCKET}/${init.storageKey}?token=${init.token}`;
      await putWithProgress(signedUrl, file, onProgress);

      // Step 3 — create the asset row referencing the uploaded object.
      return apiFetch<{ id: string }>("/admin/content/assets", {
        method: "POST",
        body: JSON.stringify({
          ...metadata,
          file_url: `${CONTENT_BUCKET}/${init.storageKey}`,
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.content.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.content.folders() });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to upload asset");
    },
  });
}
